import { useEffect } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import { Link } from "react-router-dom";

export default function PythonPage() {
  useEffect(() => {
    AOS.init({
      duration: 800,
      once: true,
      easing: "ease-in-out",
    });
    AOS.refresh();
    window.scrollTo(0, 0);
  }, []);

  const curriculum = [
    { title: "Python Basics & Syntax", icon: "💻" },
    { title: "Control Flow & Functions", icon: "⚙" },
    { title: "Object-Oriented Programming (OOP)", icon: "📘" },
    { title: "File & Exception Handling", icon: "🛡" },
    { title: "Web Dev with Django", icon: "🌐" },
    { title: "Django REST APIs", icon: "🔌" },
    { title: "Databases: SQLite & MySQL", icon: "🗄" },
    { title: "Real-time Project (E-com / Blog)", icon: "🚀" },
  ];

  const careers = [
    "Python Developer",
    "Full Stack Web Developer",
    "Automation Engineer",
    "Data Engineer",
    "Django Backend Developer",
  ];

  const projects = [
    {
      title: "E-Commerce Website",
      desc: "Build a full-featured online store with cart, checkout, and payments.",
      icon: "🛒",
    },
    {
      title: "Blog Platform",
      desc: "Create a blog system with authentication, comments, and an admin dashboard.",
      icon: "📝",
    },
  ];

  const faqs = [
    {
      question: "Is Python beginner-friendly?",
      answer:
        "Yes, Python is easy to read and ideal for beginners with no prior experience.",
    },
    {
      question: "Will we build real-world projects?",
      answer:
        "Yes! You’ll create full-stack web applications using Django and REST APIs.",
    },
    {
      question: "Is placement support available?",
      answer: "Definitely. We offer interview preparation and job referrals.",
    },
  ];

  return (
    <div className="bg-white text-gray-800 overflow-hidden">
      {/* Inline CSS */}
      <style>{`
        .card-box {
          background: #fff;
          border-radius: 12px;
          padding: 20px;
          box-shadow: 0 6px 18px rgba(15, 23, 42, 0.06);
          position: relative;
          transition: transform 250ms ease, box-shadow 250ms ease;
          will-change: transform, opacity;
          z-index: 2;
        }
        .card-box:hover {
          transform: translateY(-8px) scale(1.01);
          box-shadow: 0 14px 30px rgba(15, 23, 42, 0.12);
        }
        .corner {
          position: absolute;
          top: 0;
          right: 0;
          width: 44px;
          height: 44px;
          border-top-right-radius: 12px;
          z-index: 3;
        }
        .corner.orange { background: linear-gradient(135deg,#ff7b00,#ffb347); }
        .corner.green  { background: linear-gradient(135deg,#16a34a,#4ade80); }
        .corner.blue   { background: linear-gradient(135deg,#2563eb,#60a5fa); }
        .corner.pink   { background: linear-gradient(135deg,#ec4899,#f9a8d4); }
        .corner.purple { background: linear-gradient(135deg,#8b5cf6,#c084fc); }
        .corner.cyan   { background: linear-gradient(135deg,#06b6d4,#67e8f9); }
        .section-overlay {
          position: absolute;
          inset: 0;
          background: rgba(0,0,0,0.35);
          pointer-events: none;
          z-index: 0;
        }
        .section-inner { position: relative; z-index: 1; }
        details[open] summary::after { content: "▲"; display: inline-block; margin-left: 8px; }
        summary::after { content: "▼"; display: inline-block; margin-left: 8px; }
        details summary { cursor: pointer; }
        @media (max-width: 768px) { .card-box { padding: 16px; } }

        /* Bounce animation */
        @keyframes jump { 0%,100%{transform:translateY(0);} 50%{transform:translateY(-8px);} }
        .jump { animation: jump 2s infinite; }
        .delay-0 { animation-delay: 0s; }
        .delay-1 { animation-delay: 0.2s; }
        .delay-2 { animation-delay: 0.4s; }
        .delay-3 { animation-delay: 0.6s; }
        .delay-4 { animation-delay: 0.8s; }
        .delay-5 { animation-delay: 1s; }
        .delay-6 { animation-delay: 1.2s; }
        .delay-7 { animation-delay: 1.4s; }
      `}</style>

      {/* Hero */}
      <section
        className="relative py-20 text-white text-center bg-cover bg-center"
        style={{
          backgroundImage:
            "url('../assets/python.jpg')",
        }}
      >
        <div
          className="absolute inset-0 bg-black bg-opacity-50"
          style={{ pointerEvents: "none" }}
        ></div>
        <div className="relative max-w-4xl mx-auto px-4">
          <h1
            className="text-4xl md:text-6xl font-extrabold mb-4"
            data-aos="fade-down"
          >
            Python Software Development
          </h1>
          <p
            className="text-lg md:text-xl opacity-90 mb-6"
            data-aos="fade-up"
            data-aos-delay="150"
          >
            Master backend web development and real-time applications using
            Python & Django.
          </p>
          <a
            href="/contact"
            className="inline-block bg-white text-green-700 px-8 py-3 rounded-lg font-semibold shadow-lg hover:bg-gray-100 transition transform animate-bounce"
            data-aos="zoom-in"
            data-aos-delay="300"
          >
            🚀 Enroll Now
          </a>
        </div>
      </section>

      {/* Why Python */}
      <section className="py-16 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-10 items-center">
          <div data-aos="fade-right">
            <img
              src="../assets/PythonHeader.png"
              alt="Python Development"
              className="rounded-2xl shadow-lg"
            />
          </div>

          <div data-aos="fade-left">
            <h2 className="text-3xl font-bold mb-6">What is Python?</h2>
            <p className="text-lg leading-relaxed text-justify text-gray-700">
              Python is a high-level, interpreted programming language known for
              its simplicity and readability. It is widely used for web
              development, data science, artificial intelligence, automation,
              and software development. With its vast collection of libraries
              and frameworks, Python enables developers to build powerful
              applications quickly and efficiently.
            </p>
          </div>
        </div>
      </section>

      {/* Curriculum */}
      <section
        className="relative py-16 bg-cover bg-center"
        style={{
          backgroundImage:
            "url('../assets/pngtree-anonymous-hacker-with-computer-code-binary-interface-photo-picture-image_10194206.jpg')",
        }}
      >
        <div className="section-overlay"></div>
        <div className="section-inner relative max-w-6xl mx-auto text-center px-6">
          <h2
            className="text-3xl font-bold mb-10 text-white"
            data-aos="fade-down"
          >
            What You’ll Learn
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {curriculum.map((item, idx) => {
              const color = [
                "orange",
                "green",
                "blue",
                "pink",
                "purple",
                "cyan",
              ][idx % 6];
              const aosType = idx % 2 === 0 ? "fade-up" : "fade-down";
              return (
                <div
                  key={idx}
                  className={`card-box jump delay-${idx}`}
                  data-aos={aosType}
                  data-aos-delay={idx * 100}
                  data-aos-duration="700"
                >
                  <div className={`corner ${color}`}></div>
                  <span className="text-2xl">{item.icon}</span>
                  <h6 className="font-semibold mt-3">{item.title}</h6>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Projects */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-10" data-aos="fade-down">
            Real-time Django Projects
          </h2>
          <div className="grid sm:grid-cols-2 gap-6 p-3">
            {projects.map((proj, index) => (
              <div
                key={index}
                className={`card-box jump delay-${index}`}
                data-aos="zoom-in"
                data-aos-delay={150 + index * 120}
                data-aos-duration="800"
              >
                <div
                  className={`corner ${
                    ["blue", "pink", "purple", "cyan"][index % 4]
                  }`}
                ></div>
                <span className="text-5xl block mb-3">{proj.icon}</span>{" "}
                {/* Bigger icon */}
                <h5 className="font-semibold text-xl mb-3">
                  {proj.title}
                </h5>{" "}
                {/* Title larger */}
                <p className="text-gray-700 text-base leading-relaxed">
                  {proj.desc}
                </p>{" "}
                {/* More readable para */}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Careers */}
      <section className="bg-white py-16">
        <div className="max-w-6xl mx-auto text-center px-6">
          <h2 className="text-3xl font-bold mb-10" data-aos="fade-up-right">
            Career Paths After Python
          </h2>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6">
            {careers.map((career, index) => (
              <div
                key={index}
                className={`card-box jump delay-${index}`}
                data-aos="flip-up"
                data-aos-delay={index * 100}
              >
                <div
                  className={`corner ${
                    ["pink", "purple", "cyan", "green", "blue", "orange"][
                      index % 6
                    ]
                  }`}
                ></div>
                <span className="text-3xl mb-2 block">💼</span>
                <h5 className="font-semibold">{career}</h5>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="bg-gray-50 py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <h2
            className="text-3xl font-bold text-center mb-8"
            data-aos="fade-down"
          >
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <details
                key={idx}
                className={`card-box jump delay-${idx}`}
                data-aos="fade-up"
                data-aos-delay={idx * 80}
              >
                <summary className="flex items-center justify-between cursor-pointer font-medium">
                  {faq.question}
                </summary>
                <p className="mt-3 text-gray-600">{faq.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-green-700 py-16 text-white text-center">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Start Your Python Career Now!
          </h2>
          <p className="mb-6 opacity-90">
            Gain real skills, build real apps, and land your dream job.
          </p>
          <Link
            to="/contact"
            className="inline-block bg-white text-green-700 px-6 py-3 rounded-lg font-semibold shadow hover:bg-gray-100 transition animate-bounce"
          >
            Enroll Now
          </Link>
        </div>
      </section>
    </div>
  );
}
